export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private musicSource: AudioBufferSourceNode | null = null;
  private musicPlaying = false;

  constructor() {
    const unlock = () => {
      void this.unlock();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    this.context = new AudioContextClass();
    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = 0.45;
    this.masterGain.connect(this.context.destination);
    await this.context.resume();
    this.unlocked = true;
  }

  shoot(type: 'mg' | 'cannon' | 'at'): void {
    if (!this.context || this.context.state !== 'running' || !this.masterGain) return;

    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();

    if (type === 'mg') {
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(180, now);
      oscillator.frequency.exponentialRampToValueAtTime(80, now + 0.04);
      gain.gain.setValueAtTime(0.03, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
      oscillator.connect(gain).connect(this.masterGain);
      oscillator.start(now);
      oscillator.stop(now + 0.06);
    } else {
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(type === 'at' ? 90 : 70, now);
      oscillator.frequency.exponentialRampToValueAtTime(30, now + 0.18);
      gain.gain.setValueAtTime(type === 'at' ? 0.12 : 0.16, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
      oscillator.connect(gain).connect(this.masterGain);
      oscillator.start(now);
      oscillator.stop(now + 0.25);

      const noise = this.context.createBufferSource();
      noise.buffer = this.createNoiseBuffer(0.3);
      const noiseGain = this.context.createGain();
      noiseGain.gain.setValueAtTime(type === 'at' ? 0.08 : 0.12, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      noise.connect(noiseGain).connect(this.masterGain);
      noise.start(now);
      noise.stop(now + 0.3);
    }
  }

  explosion(scale = 1): void {
    if (!this.context || this.context.state !== 'running' || !this.masterGain) return;

    const now = this.context.currentTime;
    const noise = this.context.createBufferSource();
    noise.buffer = this.createNoiseBuffer(0.6);
    const gain = this.context.createGain();
    const filter = this.context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, now);
    filter.frequency.exponentialRampToValueAtTime(200, now + 0.5);

    gain.gain.setValueAtTime(0.12 * scale, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    noise.connect(filter).connect(gain).connect(this.masterGain);
    noise.start(now);
    noise.stop(now + 0.6);
  }

  uiClick(): void {
    if (!this.context || this.context.state !== 'running' || !this.masterGain) return;

    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(520, now);
    oscillator.frequency.exponentialRampToValueAtTime(880, now + 0.05);
    gain.gain.setValueAtTime(0.03, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    oscillator.connect(gain).connect(this.masterGain);
    oscillator.start(now);
    oscillator.stop(now + 0.1);
  }

  error(): void {
    if (!this.context || this.context.state !== 'running' || !this.masterGain) return;

    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(160, now);
    oscillator.frequency.linearRampToValueAtTime(120, now + 0.12);
    gain.gain.setValueAtTime(0.04, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    oscillator.connect(gain).connect(this.masterGain);
    oscillator.start(now);
    oscillator.stop(now + 0.16);
  }

  private createNoiseBuffer(duration: number): AudioBuffer {
    if (!this.context) throw new Error('AudioContext not initialized');
    const sampleRate = this.context.sampleRate;
    const buffer = this.context.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  startMusic(): void {
    if (!this.context || this.context.state !== 'running' || !this.masterGain || this.musicPlaying) return;

    this.stopMusic();
    this.musicGain = this.context.createGain();
    this.musicGain.gain.value = 0.08;
    this.musicGain.connect(this.masterGain);

    const buffer = this.createMusicBuffer(12);
    this.musicSource = this.context.createBufferSource();
    this.musicSource.buffer = buffer;
    this.musicSource.loop = true;
    this.musicSource.connect(this.musicGain);
    this.musicSource.start();
    this.musicPlaying = true;
  }

  stopMusic(): void {
    if (this.musicSource) {
      try {
        this.musicSource.stop();
      } catch {
        // ignore
      }
      this.musicSource.disconnect();
      this.musicSource = null;
    }
    if (this.musicGain) {
      this.musicGain.disconnect();
      this.musicGain = null;
    }
    this.musicPlaying = false;
  }

  private createMusicBuffer(duration: number): AudioBuffer {
    if (!this.context) throw new Error('AudioContext not initialized');
    const sampleRate = this.context.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const buffer = this.context.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    // Low tension drone with slow amplitude modulation
    const droneFreq = 52;
    const lfoFreq = 0.12;
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const lfo = 0.5 + 0.5 * Math.sin(t * lfoFreq * Math.PI * 2);
      const drone = Math.sin(t * droneFreq * Math.PI * 2) * 0.25 * lfo;
      const sub = Math.sin(t * (droneFreq * 0.5) * Math.PI * 2) * 0.2 * lfo;
      data[i] = drone + sub;
    }

    // Sparse military drum pattern
    const kickInterval = 0.75;
    const snareInterval = 1.5;
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const beat = t % kickInterval;
      if (beat < 0.08) {
        const env = 1 - beat / 0.08;
        const pitch = 80 - beat * 500;
        data[i] += Math.sin((t * pitch) * Math.PI * 2) * env * 0.18;
      }

      const snareBeat = t % snareInterval;
      if (snareBeat < 0.1) {
        const env = Math.exp(-snareBeat * 40);
        data[i] += (Math.random() * 2 - 1) * env * 0.12;
      }
    }

    // Distant string-like tension tone
    const stringFreq = 220;
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const pulse = Math.max(0, Math.sin(t * 0.2 * Math.PI * 2));
      const vibrato = Math.sin(t * 5 * Math.PI * 2) * 1.5;
      const tone = Math.sin((t * stringFreq + vibrato) * Math.PI * 2) * 0.015 * pulse;
      data[i] += tone;
    }

    return buffer;
  }

  dispose(): void {
    this.stopMusic();
    void this.context?.close();
    this.context = null;
    this.masterGain = null;
    this.unlocked = false;
  }
}
